import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Image, Row } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { FloatingControlComponent } from "../../../components/FormComponent";
import { formatInputDate, hoursDifference } from "../../../utils/Time";
import {
  filteredIndex,
  filteredItemByDateBetween,
  filteredItemInArray,
  flattenedArray
} from "../../../utils/ManageArray";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import {
  formatDate,
  formatDateTime,
  formatTime
} from "../../../commons/FormatValue";
import Table from "../../../components/Table";
import PrintButton from "../../../components/PrintButton";
import SearchFilter from "../../../components/SearchFilter";
import CardDisplayComponent from "../../../components/CardDisplayComponent";
import PaginationComponent from "../../../components/PaginationComponent";
import InformRepairJobsService from "../../../services/informrepairjob.service";
import InformRepairItemsService from "../../../services/informrepairitem.service";
import { ExportExcelFile } from "../../../commons/FileHandler";
import { ExcelIcon } from "../../../data/iconImage";

export default function ReportRequisition() {
  const canvasRef = useRef(null);
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [currentPage, setCurrentPage] = useState(1);
  const [PreviewPrint, setPreviewPrint] = useState(false);
  const [data, setData] = useState([]);
  const [Repair, setRepair] = useState([]);
  const [MailDate, setMailDate] = useState([]);
  const currentDate = formatInputDate(new Date())?.datetime;
  const searchInput = [
    { name: "repair_start_date", label: "ระหว่างวันที", type: "date" },
    { name: "repair_end_date", label: "ถึงวันที่", type: "date" }
  ];
  const columns = [
    { name: "ลำดับ", selector: (row) => row?.rowIndex, width: "110px" },
    {
      name: "วันที่รับแจ้งซ่อม",
      selector: (row) => row?.repair_date,
      format: (row) => formatDate(row?.repair_date),
      minWidth: "200px"
    },
    {
      name: "หมายเลขแจ้งซ่อม",
      selector: (row) => row?.repair_doc_no,
      minWidth: "200px"
    },
    {
      name: "อ้างอิง Ticket (ลูกค้า)",
      selector: (row) => row?.ref_cus_ticket_no,
      minWidth: "200px"
    },
    {
      name: "ชื่อลูกค้า/สาขา",
      selector: (row) => row?.concern_customer_items?.Cus_Name,
      minWidth: "400px"
    },
    {
      name: "รายละเอียดอุปกรณ์",
      selector: (row) => row?.ItemRepair?.repair_items?.Item_Name_TH,
      minWidth: "400px"
    },
    {
      name: "อาการที่ได้รับแจ้ง",
      selector: (row) => row?.ItemRepair?.inform_des,
      minWidth: "400px"
    },
    {
      name: "จน.ชม.ช่างทำงาน (DT)",
      selector: (row) => row?.ItemRepair?.dt_mas_items?.DT_Hrs,
      minWidth: "200px"
    },
    {
      name: "วัน/เวลา/ครบกำหนด",
      selector: (row) => row?.ItemRepair?.date_completed,
      minWidth: "200px",
      format: ({ ItemRepair }) => {
        const { dt_mas_items, date_completed } = ItemRepair || {};
        if (dt_mas_items) {
          const { DT_Hrs } = dt_mas_items;
          const d = new Date(date_completed);
          const hourDiff = hoursDifference(d, DT_Hrs)?.toISOString();
          const dt = formatInputDate(hourDiff, "ISO").datetime;
          return DT_Hrs ? formatDateTime(dt, "DD/MM/YYYY HH:MM น.") : "";
        }
      }
    }
  ];

  const handleExportExcel = () => {
    const newData = filteredItemInArray(
      data,
      "repair_date",
      MailDate?.[currentPage - 1]?.value
    );
    const filename = `รายงานการรับเมลประจำวัน-(วันที่:${formatDate(
      MailDate?.[currentPage - 1]?.value,
      "DD-MMM-YYYY"
    )})`;
    ExportExcelFile({ columns, data: newData, filename });
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: InformRepairJobsService.getAllInformRepairJobs(),
      callback: (Jobs) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest: InformRepairItemsService.getAllInformRepairItems(),
          callback: (Items) => {
            const updatedJobs = Jobs.map((j) => ({
              ...j,
              ItemRepair: Items.filter((i) => j.id === i.inform_repair_id)
            }));
            setRepair(flattenedArray(updatedJobs, "ItemRepair"));
          }
        })
    });
  }, []);

  return !PreviewPrint ? (
    <CardDisplayComponent
      header={{
        title: {
          text: "ระบุวันที่ต้องการออกรายงาน",
          className: "text-center"
        }
      }}
      body={{
        element: (
          <SearchFilter
            data={Repair}
            inputField={searchInput}
            searchCallback={(search) => {
              const [start, end] = search.filter(
                ({ name, type }) =>
                  type === "date" &&
                  ["START", "END"].some((suffix) =>
                    name.toUpperCase().includes(suffix.toUpperCase())
                  )
              );
              const filteredData = filteredItemByDateBetween(
                Repair,
                start?.value,
                end?.value,
                "repair_date"
              );
              const filteredIdx = filteredIndex(
                filteredData.map(({ repair_date }) => ({
                  name: "repair_date",
                  value: repair_date
                })),
                "value"
              );
              setMailDate(filteredIdx);
              setData(filteredData);
            }}
          />
        )
      }}
      footer={{
        className: "text-center border-top-0 bg-transparent",
        element: (
          <Button
            variant="success"
            className="me-2 px-5 py-2 text-dark rounded-pill"
            onClick={() => setPreviewPrint(!PreviewPrint)}
          >
            แสดงใบแจ้งซ่อม
          </Button>
        )
      }}
    />
  ) : (
    <>
      <Card.Body ref={canvasRef} className="position-relative">
        <Button
          variant="outline-light"
          className="px-2 border-0 rounded-circle position-absolute"
          style={{ zIndex: 10, top: "110px", right: "110px" }}
          onClick={handleExportExcel}
        >
          <Image src={ExcelIcon} alt="Excel Icon" />
        </Button>

        <PrintButton
          componentRef={canvasRef}
          button={{
            style: { zIndex: 10, top: "110px", right: "68px" }
          }}
        />

        <CardDisplayComponent
          header={{
            title: {
              text: (
                <>
                  <div>รายงานการรับเมลประจำวัน</div>
                  <div>
                    วันที่ :
                    {formatDate(
                      MailDate?.[currentPage - 1]?.value,
                      " DD-MMM-YYYY"
                    )}
                  </div>
                </>
              ),
              className: "text-center"
            }
          }}
          body={{
            className: "pt-1 p-0 mx-5",
            element: (
              <>
                <Card.Title className="h-100 mb-1 fs-2 d-flex align-items-center">
                  {user.ReportHead_TH}
                </Card.Title>

                <Table
                  data={filteredItemInArray(
                    data,
                    "repair_date",
                    MailDate?.[currentPage - 1]?.value
                  )}
                  column={columns}
                />

                <div className="d-flex mt-3_5">
                  <Row className="w-50 mx-0">
                    <Col sm="5_5" className="ps-0">
                      <FloatingControlComponent
                        readOnly
                        type="text"
                        label="รายงานโดย"
                        value={user.username}
                      />
                    </Col>
                    <Col sm="3_5">
                      <FloatingControlComponent
                        readOnly
                        type="text"
                        label="จำนวนอีเมลที่ได้รับ (ฉบับ)"
                        value=""
                      />
                    </Col>
                  </Row>
                  <Row className="w-50 mx-0 justify-content-end">
                    <Col sm="3_5">
                      <FloatingControlComponent
                        readOnly
                        type="text"
                        label="วันที่พิมพ์"
                        value={formatDate(currentDate)}
                      />
                    </Col>
                    <Col sm={2} className="pe-0">
                      <FloatingControlComponent
                        readOnly
                        type="text"
                        label="รายงานโดย"
                        value={formatTime(currentDate)}
                      />
                    </Col>
                  </Row>
                </div>
              </>
            )
          }}
        />
      </Card.Body>

      <CardDisplayComponent
        body={{
          element: (
            <PaginationComponent
              data={MailDate}
              currentPage={currentPage}
              itemsPerPage={1}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            />
          )
        }}
      />

      <CardDisplayComponent
        footer={{
          className: "text-center",
          element: (
            <Button
              variant="outline-secondary"
              className="px-5 py-2 text-dark rounded-pill"
              onClick={() => setPreviewPrint(!PreviewPrint)}
            >
              ยกเลิก
            </Button>
          )
        }}
      />
    </>
  );
}
